import { BellFilled, BellOutlined } from '@ant-design/icons'
import { Badge, Popover } from 'antd'
import React, { useEffect } from 'react'

import { NotificationsList } from './NotificationsList'
import { StopPropagation } from '@/components/common/buttons/StopPropagation'
import { getMenuItem } from '@/components/ui/common/MenuItem'
import { POLLING_INTERVALS_MS } from '@/config/ui'
import { accountUrls } from '@/config/urls/account'
import { httpFetch, useHttpFetcher } from '@/utils/http'

import {
  AccountNotification,
  PageableAccountNotification,
} from '@/types/index.d'

import styles from './Notifications.module.css'

type NotificationActions =
  | 'forceClose'
  | 'loadMoreNotifications'
  | 'onNotificationDismissed'
  | 'onNotificationRead'
  | 'onNotificationToggle'
  | 'onNotificationsListMounted'
  | 'onNotificationsListUnmounted'
  | 'onNotificationsLoaded'
  | 'onShowMoreNotifications'

type PayloadPage = {
  content: []
  empty: boolean
  last: boolean
  page_number: number
}

type PayloadToggle = {
  isOpen: boolean
}

type PayloadNotification = {
  notificationItem: AccountNotification
}

function reducer(
  state: NotificationState,
  action: {
    payload?: PayloadPage | PayloadToggle | PayloadNotification
    type: NotificationActions
  }
) {
  let newState: NotificationState
  switch (action.type) {
    case 'forceClose': {
      newState = {
        ...state,
        notifications: [],
        isNotificationLoading: true,
        isOpen: false,
        page: -1,
      }
      break
    }
    case 'onNotificationDismissed': {
      const { notificationItem } = action.payload as PayloadNotification
      const index = state.notifications.findIndex(
        (current) => current.id === notificationItem.id
      )
      state.notifications.splice(index, 1)
      newState = {
        ...state,
        isNotificationLoading: false,
        isOpen: false,
      }
      break
    }
    case 'onNotificationRead': {
      const { notificationItem } = action.payload as PayloadNotification
      const index = state.notifications.findIndex(
        (current) => current.id === notificationItem.id
      )
      const newNotifications = [...state.notifications]
      if (newNotifications?.[index]) {
        newNotifications[index].read = true
      }
      newState = {
        ...state,
        isNotificationLoading: false,
        notifications: newNotifications,
      }
      break
    }
    case 'onNotificationToggle': {
      const { isOpen } = action.payload as PayloadToggle
      newState = {
        ...state,
        isOpen,
      }
      break
    }
    case 'onNotificationsLoaded':
      {
        const { content, empty, last, page_number } =
          action.payload as PayloadPage
        newState = {
          ...state,
          empty,
          last,
          notifications:
            page_number === 0
              ? [...content]
              : [...state.notifications, ...content],
          isNotificationLoading: false,
        }
      }
      break
    case 'onNotificationsListMounted': {
      newState = {
        ...state,
        isNotificationLoading: true,
        notifications: [],
        page: 0,
      }
      break
    }
    case 'onNotificationsListUnmounted': {
      newState = {
        ...state,
        last: false,
        notifications: [],
        page: -1,
      }
      break
    }
    case 'onShowMoreNotifications':
      newState = {
        ...state,
        isNotificationLoading: true,
        page: state.page + 1,
      }
      break
    default:
      newState = {
        ...state,
        isNotificationLoading: false,
        isOpen: false,
        page: 0,
      }
  }

  return newState
}

export type NotificationState = {
  isNotificationLoading: boolean
  isOpen: boolean
  empty: boolean
  notifications: AccountNotification[]
  page: number
  last: boolean
}

export function NotificationsMenu(workspaceId: string) {
  const [notificationState, dispatch] = React.useReducer(reducer, {
    empty: true,
    isNotificationLoading: true,
    isOpen: false,
    last: false,
    notifications: [],
    page: 0,
  })

  // This is fetching the number to show in the badge
  const { data: notificationsPageOnlyNew } =
    useHttpFetcher<PageableAccountNotification>({
      customSwrConfig: {
        refreshInterval: POLLING_INTERVALS_MS['180s'],
      },
      url: accountUrls.getWorkspaceNotifications({
        workspaceId,
        params: {
          dismissed: false,
          read: false,
          page: 0,
          size: 1,
        },
      }),
    })

  useEffect(() => {
    if (
      notificationState?.isNotificationLoading &&
      notificationState?.isOpen &&
      notificationState.page > -1
    ) {
      httpFetch(
        accountUrls.getWorkspaceNotifications({
          workspaceId,
          params: {
            dismissed: false,
            read: true,
            page: notificationState.page,
            size: 3,
          },
        })
      )
        .then((response) => response.json())
        .then((data) => {
          dispatch({
            type: 'onNotificationsLoaded',
            payload: {
              content: data?.content,
              empty: data?.empty,
              last: data?.last,
              page_number: data?.pageable.page_number,
            },
          })
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    notificationState?.isNotificationLoading,
    notificationState?.isOpen,
    notificationState?.page,
  ])

  const handleNotificationToggle = (isOpen: boolean) =>
    dispatch({
      type: 'onNotificationToggle',
      payload: { isOpen },
    })

  const handleShowMoreClick = () =>
    dispatch({
      type: 'onShowMoreNotifications',
    })

  const handleDismissNotificationClick = (
    notificationItem: AccountNotification
  ) => {
    dispatch({
      type: 'onNotificationDismissed',
      payload: {
        notificationItem,
      },
    })
  }

  const handleMount = () =>
    dispatch({
      type: 'onNotificationsListMounted',
      payload: undefined,
    })

  const handleUnmount = () =>
    dispatch({
      type: 'onNotificationsListUnmounted',
      payload: undefined,
    })

  const handleCloseNotification = () =>
    dispatch({
      type: 'onNotificationToggle',
      payload: { isOpen: false },
    })

  const handleNotificationRead = (notificationItem: AccountNotification) =>
    dispatch({
      type: 'onNotificationRead',
      payload: { notificationItem },
    })

  return getMenuItem(
    '',
    'notification',
    <StopPropagation>
      <Popover
        content={
          <NotificationsList
            callbacks={{
              close: handleCloseNotification,
              mount: handleMount,
              unmount: handleUnmount,
            }}
            notificationState={notificationState}
            onNotificationRead={handleNotificationRead}
            onShowMoreClick={handleShowMoreClick}
            onDismissItemClick={handleDismissNotificationClick}
            showLoadMore={
              !notificationState?.isNotificationLoading &&
              !notificationState?.last
            }
          />
        }
        onOpenChange={handleNotificationToggle}
        open={notificationState.isOpen}
        overlayClassName={styles.notifications}
        trigger={'click'}
        destroyTooltipOnHide
      >
        <Badge count={notificationsPageOnlyNew?.total_elements}>
          {notificationState.isOpen ? (
            <BellFilled color="primary" />
          ) : (
            <BellOutlined />
          )}
        </Badge>
      </Popover>
    </StopPropagation>
  )
}
